import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin } from "lucide-react";
import { api } from "@/lib/api";

function fmt(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n;
}

export default function TalentsPage() {
  const [talents, setTalents] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("Todas");
  const [category, setCategory] = useState("Todas");

  useEffect(() => { api.get("/talents").then(setTalents).catch(() => setTalents([])); }, []);

  const cities = useMemo(() => ["Todas", ...Array.from(new Set(talents.map((t) => t.city).filter(Boolean)))], [talents]);
  const categories = useMemo(() => ["Todas", ...Array.from(new Set(talents.flatMap((t) => (t.category || "").split("/").map((s) => s.trim())).filter(Boolean)))], [talents]);

  const filtered = talents.filter((t) => {
    if (q && !t.artist_name.toLowerCase().includes(q.toLowerCase())) return false;
    if (city !== "Todas" && t.city !== city) return false;
    if (category !== "Todas" && !(t.category || "").includes(category)) return false;
    return true;
  });

  return (
    <div data-testid="talents-page">
      <section className="pt-20 pb-10 lg:pt-32 lg:pb-12 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 font-bold">Talentos</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] mb-8 max-w-5xl">
            Una red nacional <br className="hidden md:block" />de <span className="gradient-text">creadores reales</span>.
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl">
            Influencers, figuras digitales, streamers y conductores que mueven audiencias en distintas ciudades de Bolivia.
          </p>
        </div>
      </section>

      <section className="py-10 sticky top-16 lg:top-20 glass z-30 border-b border-border" data-testid="talents-filters">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre..."
              data-testid="talents-search"
              className="w-full pl-10 pr-4 h-11 rounded-sm bg-background border border-border focus:border-primary outline-none text-sm"
            />
          </div>
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="talents-city-filter" className="h-11 px-3 rounded-sm bg-background border border-border focus:border-primary outline-none text-sm">
            {cities.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="talents-category-filter" className="h-11 px-3 rounded-sm bg-background border border-border focus:border-primary outline-none text-sm">
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-20">No se encontraron talentos.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {filtered.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
                >
                  <Link to={`/talentos/${t.id}`} className="block group" data-testid={`talent-card-${t.id}`}>
                    <div className="relative aspect-[3/4] overflow-hidden rounded-sm bg-secondary border border-border">
                      {t.photo && <img src={t.photo} alt={t.artist_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80" />
                      <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1">
                        {(t.category || "").split("/").slice(0, 2).map((c, idx) => (
                          <span key={idx} className="bg-primary/90 text-primary-foreground text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm">{c.trim()}</span>
                        ))}
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <div className="font-display font-black text-lg leading-tight">{t.artist_name}</div>
                        <div className="flex items-center gap-1 text-xs text-white/80 mt-1"><MapPin className="w-3 h-3" /> {t.city}</div>
                        <div className="text-xs text-white/90 mt-2 font-semibold">IG {fmt(t.followers_instagram)} · TT {fmt(t.followers_tiktok)}</div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
